import { useEffect } from "react";
import { Routes, Route, useLocation, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Hero } from "@/components/site/Hero";
import { Studio } from "@/components/site/Studio";
import { Projects } from "@/components/site/Projects";
import { BeforeAfter } from "@/components/site/BeforeAfter";
import { CostGuide } from "@/components/site/CostGuide";
import { Reels } from "@/components/site/Reels";
import { Services } from "@/components/site/Services";
import { Testimonials } from "@/components/site/Testimonials";
import { SiteVisit } from "@/components/site/SiteVisit";
import { Contact } from "@/components/site/Contact";
import { WorkGallery } from "@/components/site/WorkGallery";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { EASE } from "@/components/site/motion";
import StaffPage from "./StaffPage";

// Dark editorial banner used at the top of inner pages.
const PageIntro = ({ eyebrow, title, text, testId }) => (
  <section className="bg-[#161614] text-[#FBFBF9] pt-16 pb-20 lg:pt-24 lg:pb-28" data-testid={testId}>
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Eyebrow className="mb-6 !text-stone-400">{eyebrow}</Eyebrow>
      <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl">
        {title.split(" ").map((w, i) => (
          <span key={i} className="inline-block overflow-hidden pb-[0.1em] -mb-[0.1em] mr-[0.25em]">
            <motion.span className="inline-block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.15 + i * 0.06, ease: EASE }}>{w}</motion.span>
          </span>
        ))}
      </h1>
      {text && <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.8, ease: EASE }} className="mt-6 max-w-xl text-stone-400 font-light leading-relaxed">{text}</motion.p>}
    </div>
  </section>
);

// Cross-link strip at the bottom of each page so visitors keep moving.
const NextPage = ({ to, label, title }) => (
  <Reveal className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
    <Link to={to} className="group flex items-center justify-between gap-6 border-t border-stone-300 pt-8" data-testid={`next-page-${to.replace("/", "") || "home"}`}>
      <div><p className="font-mono text-[11px] uppercase tracking-[0.25em] text-stone-500">{label}</p><p className="font-serif text-3xl sm:text-4xl text-stone-900 mt-2 transition-transform duration-500 group-hover:translate-x-2">{title}</p></div>
      <span className="w-12 h-12 shrink-0 rounded-full border border-stone-400 flex items-center justify-center transition-all duration-300 group-hover:bg-stone-900 group-hover:text-[#FBFBF9] group-hover:border-stone-900"><ArrowRight size={18} /></span>
    </Link>
  </Reveal>
);

const HomePage = () => (<><Hero /><Studio /><WorkGallery limit={6} compact /><Reels /><Testimonials /><NextPage to="/projects" label="Next" title="See the projects" /></>);
const ProjectsPage = () => (<><PageIntro eyebrow="Projects" title="Homes we have drawn and built." text="Completed houses, renovations and ongoing sites across Kerala — plus the latest renders and photos straight from our team." testId="projects-page-intro" /><Projects /><BeforeAfter /><WorkGallery /><NextPage to="/services" label="Next" title="What we do" /></>);
const ServicesPage = () => (<><PageIntro eyebrow="Services" title="Design, build and finish — under one roof." text="From the first sketch to the last coat of microcement." testId="services-page-intro" /><Services /><CostGuide /><NextPage to="/site-visit" label="Next" title="Book a site visit" /></>);
const SiteVisitPage = () => (<><PageIntro eyebrow="Site visit" title="Bring Gokul to your plot." text="Transparent fees by district, instant booking and a receipt in your inbox." testId="site-visit-page-intro" /><SiteVisit /><NextPage to="/contact" label="Next" title="Start your project" /></>);
const ContactPage = () => (<><PageIntro eyebrow="Contact" title="Tell us about your home." text="Thalassery studio · Kerala and beyond." testId="contact-page-intro" /><Contact /></>);
const NotFound = () => (<section className="max-w-7xl mx-auto px-6 lg:px-10 py-40" data-testid="not-found"><Eyebrow className="mb-6">404</Eyebrow><h1 className="font-serif text-5xl text-stone-900">This page is still on the drawing board.</h1><Link to="/" className="btn-dark mt-10">Back home</Link></section>);

const ROUTES = [["/", HomePage], ["/projects", ProjectsPage], ["/services", ServicesPage], ["/site-visit", SiteVisitPage], ["/contact", ContactPage], ["/staff", StaffPage], ["*", NotFound]];

export const SiteRoutes = () => {
  const location = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [location.pathname]);
  return (
    <AnimatePresence mode="wait">
      <motion.main key={location.pathname} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
        {/* Blueprint curtain wipes down then up on every page change */}
        <motion.div aria-hidden="true" className="fixed inset-0 z-[90] bg-[#161614] pointer-events-none origin-top" initial={{ scaleY: 1 }} animate={{ scaleY: 0 }} exit={{ scaleY: 1, transition: { duration: 0.35, ease: EASE } }} transition={{ duration: 0.6, ease: EASE }} style={{ transformOrigin: "top" }} />
        <Routes location={location}>
          {ROUTES.map(([path, Page]) => <Route key={path} path={path} element={<Page />} />)}
        </Routes>
      </motion.main>
    </AnimatePresence>
  );
};
